import { createContext, ReactNode, useContext, useMemo } from "react";
import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { persist } from "zustand/middleware";
import { useAuth0, User } from "@auth0/auth0-react";
import { useFeatureFlag } from "configcat-react";
import { FeatureFlag } from "./FeatureFlag.model";

export function isQuack(user?: User): boolean {
  if (user == null || user.owner == null || user.email == null) {
    return false;
  }

  return ["quack"].includes(user.owner) || user.email.endsWith("@quack.ai");
}

type FlagOverrides = Partial<Record<FeatureFlag, boolean>>;

interface FlagOverridesStore {
  overrides: FlagOverrides;
  addOverride: (key: FeatureFlag, value: boolean) => void;
  updateOverride: (key: FeatureFlag, value: boolean) => void;
  removeOverride: (key: FeatureFlag) => void;
  clearOverrides: () => void;
}

const buildFlagOverridesStore = () =>
  create<FlagOverridesStore>()(
    persist(
      immer((set) => ({
        overrides: {},
        addOverride: (key, value) =>
          set((state) => {
            if (!(key in state.overrides)) {
              state.overrides[key] = value;
            }
          }),
        updateOverride: (key, value) =>
          set((state) => {
            if (key in state.overrides) {
              state.overrides[key] = value;
            }
          }),
        removeOverride: (key) =>
          set((state) => {
            delete state.overrides[key];
          }),
        clearOverrides: () =>
          set((state) => {
            state.overrides = {};
          }),
      })),
      {
        name: "quack-flag-overrides",
        partialize: (state) => ({ overrides: state.overrides }),
      }
    )
  );

const FlagOverridesContext = createContext<ReturnType<
  typeof buildFlagOverridesStore
> | null>(null);

export const FlagOverridesProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const store = useMemo(() => buildFlagOverridesStore(), []);

  return (
    <FlagOverridesContext.Provider value={store}>
      {children}
    </FlagOverridesContext.Provider>
  );
};

export function useFlagOverridesStore<T>(
  selector: (state: FlagOverridesStore) => T
): T {
  const store = useContext(FlagOverridesContext);
  if (!store) {
    throw new Error(
      "useOverrideStore must be used within an <OverrideProvider />"
    );
  }

  return store(selector);
}

export function useFlagWithLoading(
  key: FeatureFlag,
  defaultValue: boolean = false
): { value: boolean; loading: boolean } {
  const override = useFlagOverridesStore((s) => s.overrides[key]);

  const { user } = useAuth0();
  const { value: configValue, loading } = useFeatureFlag(key, defaultValue, {
    identifier: user?.owner ?? "",
    email: user?.email ?? "",
    custom: user ?? {},
  });

  const value = isQuack(user) && override !== undefined ? override : configValue;
  return { value, loading };
}

export function useFlag(
  key: FeatureFlag,
  defaultValue: boolean = false
): boolean {
  const { value } = useFlagWithLoading(key, defaultValue);
  return value;
}
